import { useCallback, useEffect, useRef, useState } from 'react';

import { isTablet as defineIsTablet } from 'react-native-device-info';
import Orientation, { OrientationType } from 'react-native-orientation-locker';

export enum ORIENTATION {
    PORTRAIT = 'PORTRAIT',
    LANDSCAPE_RIGHT = 'LANDSCAPE-RIGHT',
    LANDSCAPE_LEFT = 'LANDSCAPE-LEFT',
    FACE_UP = 'FACE-UP',
    FACE_DOWN = 'FACE-DOWN',
    PORTRAIT_UPSIDEDOWN = 'PORTRAIT-UPSIDEDOWN',
    UNKNOWN = 'UNKNOWN',
}

const isTablet = defineIsTablet();

const DEFAULT_ORIENTATION = isTablet
    ? ORIENTATION.LANDSCAPE_LEFT
    : ORIENTATION.PORTRAIT;

export const DEFAULT_ORIENTATION_TYPE = isTablet
    ? OrientationType['LANDSCAPE-LEFT']
    : OrientationType.PORTRAIT;

export const lockToDefaultOrientation = () => {
    if (isTablet) {
        Orientation.lockToLandscape();
    } else {
        Orientation.lockToPortrait();
    }
};

export const mapOrientationToOrientationType = (
    orientation: ORIENTATION,
): OrientationType => {
    switch (orientation) {
        case ORIENTATION.PORTRAIT:
            return OrientationType['PORTRAIT'];
        case ORIENTATION.LANDSCAPE_RIGHT:
            return OrientationType['LANDSCAPE-RIGHT'];
        case ORIENTATION.LANDSCAPE_LEFT:
            return OrientationType['LANDSCAPE-LEFT'];
        case ORIENTATION.FACE_UP:
            return OrientationType['FACE-UP'];
        case ORIENTATION.FACE_DOWN:
            return OrientationType['FACE-DOWN'];
        case ORIENTATION.PORTRAIT_UPSIDEDOWN:
            return OrientationType['PORTRAIT-UPSIDEDOWN'];
        case ORIENTATION.UNKNOWN:
            return OrientationType['UNKNOWN'];
        default:
            return OrientationType['PORTRAIT'];
    }
};

export const lockToPortrait = () => {
    Orientation.lockToPortrait();
};

export const lockToLandscapeLeft = () => {
    Orientation.lockToLandscapeLeft();
};

export const lockToLandscapeRight = () => {
    Orientation.lockToLandscapeRight();
};

export const lockToLandscape = () => {
    Orientation.lockToLandscape();
};

export const useLockedOrientation = () => {
    const [lockedOrientation, setLockedOrientation] =
        useState(DEFAULT_ORIENTATION);

    const lockListener = useCallback((orientation) => {
        setLockedOrientation(orientation);
    }, []);

    useEffect(() => {
        Orientation.getOrientation(lockListener);
        Orientation.addLockListener(lockListener);

        return () => {
            Orientation.removeLockListener(lockListener);
        };
    }, []);

    return lockedOrientation;
};

export const useDeviceOrientation = () => {
    const [orientation, setOrientation] = useState(DEFAULT_ORIENTATION_TYPE);

    const orientationListener = useCallback((newOrientation) => {
        setOrientation(newOrientation);
    }, []);

    useEffect(() => {
        Orientation.addOrientationListener(orientationListener);

        return () => {
            Orientation.removeOrientationListener(orientationListener);
        };
    }, []);

    return orientation;
};

export const useDeviceOrientationEffect = (
    effect: (orientation: OrientationType) => void,
) => {
    const isFirstRender = useRef(true);

    useEffect(() => {
        if (isFirstRender.current === true) {
            isFirstRender.current = false;
            Orientation.getDeviceOrientation(effect);
        }

        Orientation.addDeviceOrientationListener(effect);

        return () => {
            Orientation.removeDeviceOrientationListener(effect);
        };
    }, [effect]);
};
